import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { capturePng, downloadCanvas } from '../../../../utils/pngExport'
import { Button, LinearProgress } from '@mui/material'
import { PrintRounded, ImageRounded } from '@mui/icons-material'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import ReportSheet from './ReportSheet'
import { ui } from '../../../../utils/uiTokens'

interface SheetLoaderProps {
    userSubjectEnrollId: number
    monthNumber?: number
    weekNumber?: number
    onLoaded: (id: number) => void
}

const SheetLoader: React.FC<SheetLoaderProps> = ({
    userSubjectEnrollId,
    monthNumber,
    weekNumber,
    onLoaded,
}) => {
    const { data: report, isSuccess, isError } = ratingApiSlice.useGetWeekReportQuery({
        userSubjectEnrollId,
        monthNumber,
        weekNumber,
    })

    useEffect(() => {
        if (isSuccess || isError) onLoaded(userSubjectEnrollId)
    }, [isSuccess, isError])

    if (!report) return null

    const name = `${report.summary.firstName || ''}_${report.summary.lastName || ''}`
        .trim()
        .replace(/\s+/g, '_')

    return (
        <SheetWrap className="png-sheet" data-name={name}>
            <ReportSheet report={report} monthNumber={monthNumber} weekNumber={weekNumber} />
        </SheetWrap>
    )
}

const MassPrintPage = () => {
    const { formatMessage } = useIntl()
    const [searchParams] = useSearchParams()

    const ids = (searchParams.get('ids') || '')
        .split(',')
        .map(Number)
        .filter(Boolean)
    const monthNumber = Number(searchParams.get('monthNumber')) || undefined
    const weekNumber = Number(searchParams.get('weekNumber')) || undefined
    const mode = searchParams.get('mode') === 'png' ? 'png' : 'pdf'

    const [loaded, setLoaded] = useState<number[]>([])
    const [saving, setSaving] = useState(false)
    const didAuto = useRef(false)

    const onLoaded = (id: number) =>
        setLoaded((prev) => (prev.includes(id) ? prev : [...prev, id]))

    const ready = ids.length > 0 && loaded.length >= ids.length

    const savePng = async () => {
        setSaving(true)
        const sheets = Array.from(document.querySelectorAll<HTMLElement>('.png-sheet'))
        for (let i = 0; i < sheets.length; i++) {
            const el = sheets[i]
            // eslint-disable-next-line no-await-in-loop
            const canvas = await capturePng(el)
            // eslint-disable-next-line no-await-in-loop
            await downloadCanvas(canvas, `${el.dataset.name || 'report'}.png`)
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 400))
        }
        setSaving(false)
    }

    // Один раз по готовности запускаем выбранный режим
    useEffect(() => {
        if (!ready || didAuto.current) return
        didAuto.current = true
        if (mode === 'png') {
            savePng()
        } else {
            const t = setTimeout(() => window.print(), 600)
            return () => clearTimeout(t)
        }
    }, [ready])

    if (!ids.length) return <Empty>{formatMessage({ id: 'noData' })}</Empty>

    return (
        <Wrapper className="print-area">
            <Toolbar className="no-print">
                {!ready ? (
                    <LinearProgress sx={{ flex: 1 }} />
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            startIcon={<PrintRounded />}
                            onClick={() => window.print()}
                        >
                            PDF ({ids.length})
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<ImageRounded />}
                            disabled={saving}
                            onClick={savePng}
                        >
                            PNG ({ids.length})
                        </Button>
                    </>
                )}
            </Toolbar>

            {ids.map((id) => (
                <SheetLoader
                    key={id}
                    userSubjectEnrollId={id}
                    monthNumber={monthNumber}
                    weekNumber={weekNumber}
                    onLoaded={onLoaded}
                />
            ))}
        </Wrapper>
    )
}

export default MassPrintPage

const Wrapper = styled.div`
    margin: 10px;

    @media print {
        margin: 0;

        .no-print {
            display: none !important;
        }
    }
`

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    max-width: 900px;
    margin: 0 auto 16px;
`

const SheetWrap = styled.div`
    max-width: 940px;
    margin: 0 auto 24px;

    @media print {
        max-width: none;
        margin: 0;
        page-break-after: always;

        &:last-child {
            page-break-after: auto;
        }
    }
`

const Empty = styled.div`
    padding: 64px 0;
    text-align: center;
    font-size: 14px;
    color: ${ui.muted};
`
